import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  Collapse,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Loading from '../../../components/loading';
import { useParams, useNavigate } from 'react-router-dom';
import toMessageText from '../../../components/messageText';
import { useSaveGeneralEvolution } from './hooks/useSaveGeneralEvolution';
import { useRectifyGeneralEvolution } from './hooks/useRectifyGeneralEvolution';

dayjs.extend(customParseFormat);

/**
 * Cadastro de evolução geral. Com `evolutionToRectify`, o mesmo modal retifica uma evolução existente:
 * vem preenchido e o registro anterior fica guardado no histórico de retificações.
 */
export default function GeneralEvolutionModal({
  open,
  onClose,
  onEvolutionSave,
  evolutionToRectify,
}) {
  const { patientId } = useParams();

  const { save: saveGeneralEvolutionRequest } = useSaveGeneralEvolution();
  const { rectify: rectifyGeneralEvolutionRequest } = useRectifyGeneralEvolution();

  const [isLoading, setLoading] = useState(false);

  const navigate = useNavigate();

  const [evolutionDate, setEvolutionDate] = useState(dayjs());
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const isRectifying = !!evolutionToRectify;

  useEffect(() => {
    if (!open || !evolutionToRectify) return;
    setEvolutionDate(evolutionToRectify.data ? dayjs(evolutionToRectify.data, 'DD/MM/YYYY') : null);
    setDescription(evolutionToRectify.evolucao ?? '');
    setError('');
  }, [open, evolutionToRectify]);

  const handleRequestError = (returnFromApi) => {
    setLoading(false);
    console.error('ops! ocorreu um erro' + returnFromApi);
    if (returnFromApi.response?.status === 401) {
      navigate('/expired-session');
      return;
    }
    setError(toMessageText(returnFromApi.response?.data, 'Ocorreu um erro ao salvar a evolução.'));
  };

  const saveGeneralEvolution = (event) => {
    event.preventDefault();
    setError('');

    if (!description.trim() || !evolutionDate) {
      setError('Todos os campos são obrigatórios!');
      return;
    }

    setLoading(true);
    const request = isRectifying
      ? rectifyGeneralEvolutionRequest(evolutionToRectify.id, {
          date: dayjs(evolutionDate).format('DD/MM/YYYY'),
          description: description,
        })
      : saveGeneralEvolutionRequest({
          pacienteId: patientId,
          data: dayjs(evolutionDate).format('DD/MM/YYYY'),
          evolucao: description,
        });

    request
      .then((returnFromApi) => {
        setLoading(false);
        onEvolutionSave(returnFromApi);
        cleanFields();
      })
      .catch(handleRequestError);
  };

  function cleanFields() {
    setDescription('');
    setEvolutionDate(dayjs());
    setError('');
  }

  const closeModal = () => {
    if (isRectifying) cleanFields();
    onClose();
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />

      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
        <Loading isToHidden={isLoading === false} />

        <DialogTitle>
          <Typography variant="outlined" sx={{ fontWeight: 'bold' }}>
            {isRectifying ? 'Retificar Evolução Geral' : 'Adicionar Evolução Geral'}
          </Typography>
          {isRectifying && (
            <Typography variant="body2" color="text.secondary">
              O registro anterior fica guardado no histórico de retificações.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Collapse in={!!error} sx={{ mt: 1, mb: 1 }}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Collapse>

          <Grid>
            <Grid size={1}>
              <DatePicker
                label="Data da Evolução"
                value={evolutionDate}
                onChange={(newValue) => setEvolutionDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid size={8}>
              <TextField
                data-cy="general-evolution-description"
                fullWidth
                label="Descrição"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancelar</Button>
          <Button
            data-cy="save-general-evolution-btn"
            onClick={saveGeneralEvolution}
            variant="contained"
          >
            {isRectifying ? 'Retificar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

GeneralEvolutionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEvolutionSave: PropTypes.func.isRequired,
  evolutionToRectify: PropTypes.shape({
    id: PropTypes.number.isRequired,
    data: PropTypes.string,
    evolucao: PropTypes.string,
  }),
};

GeneralEvolutionModal.defaultProps = {
  evolutionToRectify: null,
};
